import React, { useState, useRef, useCallback, useEffect } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import SpeedIcon from '@mui/icons-material/Speed';
import { pingServer, downloadTest, uploadTest } from "../../api/helper_api";
import { useGetMenuMaster } from "../../api/menu";

import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';

const getDownloadColor = (val, duration) => {
  if (val === null) return "#94a3b8";
  if (val === 0 || (duration && duration >= 60)) return "#dc2626"; // Red (Poor)
  if (val >= 25) return "#16a34a"; // Green (Good >= 25 Mbps)
  if (val >= 10) return "#ca8a04"; // Yellow/Amber (Normal >= 10 Mbps)
  return "#dc2626";                // Red (Poor < 10 Mbps)
};

const getUploadColor = (val, duration) => {
  if (val === null) return "#94a3b8";
  if (val === 0 || (duration && duration >= 60)) return "#dc2626"; // Red (Poor)
  if (val >= 10) return "#16a34a"; // Green (Good >= 10 Mbps)
  if (val >= 3) return "#ca8a04";  // Yellow/Amber (Normal >= 3 Mbps)
  return "#dc2626";                // Red (Poor < 3 Mbps)
};

const getDownloadText = (val, duration) => {
  if (val === null) return "—";
  if (val === 0 || (duration && duration >= 60)) return "Poor Connection";
  return `${val.toFixed(2)} Mbps`;
};

const getUploadText = (val, duration) => {
  if (val === null) return "—";
  if (val === 0 || (duration && duration >= 60)) return "Poor Connection";
  return `${val.toFixed(2)} Mbps`;
};

export default function SidebarSpeedTest() {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("");
  const [download, setDownload] = useState(null);
  const [upload, setUpload] = useState(null);
  const [downloadTime, setDownloadTime] = useState(null);
  const [uploadTime, setUploadTime] = useState(null);
  const abortRef = useRef(null);

  const reset = () => {
    setDownload(null);
    setUpload(null);
    setDownloadTime(null);
    setUploadTime(null);
    setPhase("");
  };

  const measurePing = useCallback(async () => {
    const s = [];
    for (let i = 0; i < 5; i++) {
      try {
        const ms = await pingServer();
        s.push(ms);
      } catch (_) { }
    }
    s.sort((a, b) => a - b);
    return s[Math.floor(s.length / 2)];
  }, []);

  const measureDownload = useCallback(async () => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const speed = await downloadTest(10000000, ctrl.signal);
      return speed;
    } catch (err) {
      if (err.name === "AbortError" || err.message === "canceled") return null;
      throw err;
    }
  }, []);

  const measureUpload = useCallback(async () => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const SIZE = 10 * 1024 * 1024;
    const blob = new Blob([new Uint8Array(SIZE)]);
    try {
      const speed = await uploadTest(blob, ctrl.signal);
      return speed;
    } catch (err) {
      if (err.name === "AbortError" || err.message === "canceled") return null;
      throw err;
    }
  }, []);

  const runTest = useCallback(async () => {
    reset();
    setRunning(true);
    try {
      setPhase("ping");
      await measurePing();
    } catch (err) {
      console.error("Ping failed:", err);
    }

    // Download Speed Test
    setPhase("download");
    const tStartDl = performance.now();
    try {
      const dl = await measureDownload();
      if (dl === null) {
        setDownload(null);
        setDownloadTime(null);
      } else {
        const tEndDl = performance.now();
        const dlTimeSec = (tEndDl - tStartDl) / 1000;
        if (dlTimeSec >= 60) {
          setDownload(0);
          setDownloadTime(dlTimeSec);
          sessionStorage.setItem("last_speed_test_download", "0.00");
          sessionStorage.setItem("last_speed_test_download_time", dlTimeSec.toFixed(2));
        } else {
          setDownload(dl);
          setDownloadTime(dlTimeSec);
          sessionStorage.setItem("last_speed_test_download", dl.toFixed(2));
          sessionStorage.setItem("last_speed_test_download_time", dlTimeSec.toFixed(2));
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") console.error("Download test failed:", err);
      const tEndDl = performance.now();
      const dlTimeSec = Math.min(60, (tEndDl - tStartDl) / 1000);
      setDownload(0); // Poor
      setDownloadTime(dlTimeSec || 60);
      sessionStorage.setItem("last_speed_test_download", "0.00");
      sessionStorage.setItem("last_speed_test_download_time", (dlTimeSec || 60).toFixed(2));
    }

    // Upload Speed Test
    setPhase("upload");
    const tStartUl = performance.now();
    try {
      const ul = await measureUpload();
      if (ul === null) {
        setUpload(null);
        setUploadTime(null);
      } else {
        const tEndUl = performance.now();
        const ulTimeSec = (tEndUl - tStartUl) / 1000;
        if (ulTimeSec >= 60) {
          setUpload(0);
          setUploadTime(ulTimeSec);
          sessionStorage.setItem("last_speed_test_upload", "0.00");
          sessionStorage.setItem("last_speed_test_upload_time", ulTimeSec.toFixed(2));
        } else {
          setUpload(ul);
          setUploadTime(ulTimeSec);
          sessionStorage.setItem("last_speed_test_upload", ul.toFixed(2));
          sessionStorage.setItem("last_speed_test_upload_time", ulTimeSec.toFixed(2));
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") console.error("Upload test failed:", err);
      const tEndUl = performance.now();
      const ulTimeSec = Math.min(60, (tEndUl - tStartUl) / 1000);
      setUpload(0); // Poor
      setUploadTime(ulTimeSec || 60);
      sessionStorage.setItem("last_speed_test_upload", "0.00");
      sessionStorage.setItem("last_speed_test_upload_time", (ulTimeSec || 60).toFixed(2));
    }

    setPhase("done");
    setRunning(false);
  }, [measurePing, measureDownload, measureUpload]);

  useEffect(() => {
    if (!drawerOpen) {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      return;
    }

    // Run test immediately on mount / drawer open
    runTest();

    // Trigger test every 2 minutes
    const interval = setInterval(() => {
      runTest();
    }, 120000);

    return () => {
      clearInterval(interval);
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [drawerOpen, runTest]);

  const downloadColor = getDownloadColor(download, downloadTime);
  const uploadColor = getUploadColor(upload, uploadTime);

  if (!drawerOpen) return null;

  return (
    <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.15)', width: '100%' }}>
      <Box sx={{ display: "flex", gap: 1, marginBottom: 1.5 }}>
        {/* Download Speed Card */}
        <Box sx={{
          flex: 1,
          display: "flex", alignItems: "center", gap: 1,
          padding: "8px 8px",
          borderRadius: "8px",
          // background: "rgba(255, 255, 255, 0.08)",
          // border: "1px solid rgba(255, 255, 255, 0.12)",
          // boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          minWidth: 0,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            background: "rgba(255, 255, 255, 0.14)",
            borderColor: "rgba(255, 255, 255, 0.2)",
            boxShadow: "0 6px 16px rgba(0,0,0,0.12)"
          }
        }}>
          <Tooltip title="Download Speed" arrow placement="top">
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 26, height: 26, borderRadius: "50%",
              background: "#ffffff", flexShrink: 0,
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
            }}>
              <DownloadIcon sx={{ fontSize: 15, color: downloadColor, stroke: 'currentColor', strokeWidth: 1.5 }} />
            </Box>
          </Tooltip>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            {/* <Typography sx={{ fontSize: 8, fontWeight: 700, color: 'rgba(255, 255, 255, 0.55)', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.1 }}>
              Download
            </Typography> */}
            <Typography sx={{ fontSize: 8, fontStyle: 'italic', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: 0.2 }}>
              {getDownloadText(download, downloadTime)}
              {download !== null && download !== 0 && downloadTime !== null && downloadTime < 60 ? ` (${downloadTime.toFixed(1)}s)` : ""}
            </Typography>
          </Box>
        </Box>

        {/* Upload Speed Card */}
        <Box sx={{
          flex: 1,
          display: "flex", alignItems: "center", gap: 1,
          padding: "8px 8px",
          borderRadius: "8px",
          // background: "rgba(255, 255, 255, 0.08)",
          // border: "1px solid rgba(255, 255, 255, 0.12)",
          // boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          minWidth: 0,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            background: "rgba(255, 255, 255, 0.14)",
            borderColor: "rgba(255, 255, 255, 0.2)",
            boxShadow: "0 6px 16px rgba(0,0,0,0.12)"
          }
        }}>
          <Tooltip title="Upload Speed" arrow placement="top">
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 26, height: 26, borderRadius: "50%",
              background: "#ffffff", flexShrink: 0,
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
            }}>
              <FileUploadIcon sx={{ fontSize: 15, color: uploadColor, stroke: 'currentColor', strokeWidth: 1.5 }} />
            </Box>
          </Tooltip>
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            {/* <Typography sx={{ fontSize: 8, fontWeight: 700, color: 'rgba(255, 255, 255, 0.55)', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.1 }}>
              Upload
            </Typography> */}
            <Typography sx={{ fontSize: 8, fontStyle: 'italic', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: 0.2 }}>
              {getUploadText(upload, uploadTime)}
              {upload !== null && upload !== 0 && uploadTime !== null && uploadTime < 60 ? ` (${uploadTime.toFixed(1)}s)` : ""}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Auto-update notice */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1, mb: running ? 1.5 : 0 }}>
        <Typography sx={{ fontSize: 9, color: 'rgba(255, 255, 255, 0.55)', fontWeight: 500, letterSpacing: '0.2px' }}>
          Auto-updates every 2 minutes
        </Typography>
      </Box>

      {/* Status banner */}
      {running && (
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
          padding: "6px 12px", background: "rgba(255, 255, 255, 0.1)", borderRadius: "6px",
          border: "1px solid rgba(255, 255, 255, 0.08)", mt: 1.5,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: "50%", background: "#4ade80",
            display: "inline-block", animation: "sidebarPulse 1.2s ease-in-out infinite",
          }} />
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#ffffff" }}>
            {phase === "ping" && "Measuring ping…"}
            {phase === "download" && "Testing download speed…"}
            {phase === "upload" && "Testing upload speed…"}
          </Typography>
        </Box>
      )}

      <style>{`
        @keyframes sidebarPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
      `}</style>
    </Box>
  );
}
